import React, { Fragment, useState, useEffect } from 'react'
import moment from 'moment'
import injectStyles from 'react-jss'
import { useDispatch, useSelector } from 'react-redux'

import MenuItem from '@material-ui/core/MenuItem'
import FormControl from '@material-ui/core/FormControl'
import Select from '@material-ui/core/Select'
import InputLabel from '@material-ui/core/InputLabel'
import { PageTitle } from 'Components/PageTitle'
import { getCountries } from 'Reducers/Countries/actions'
import SimpleTableTable from 'Components/SimpleTableTable'
import Button from '../../../Components/Buttons/Button/index'
import { escapeCsvString } from '../../../shared/csv'
import LoadingIndicator from '../../../Components/LoadingIndicator/LoadingIndicator'
import { BreadcrumbsNetPromoterScore } from './BreadcrumbsNetPromoterScore'
import { apiClient } from 'Module/API/Client'
import { downloadStringAsFile } from '../../../Utils/Misc'
import { isDefined } from '../../../shared/libraries/utils/object'
import {
  dropTimeZoneFromDate,
  getWeekNumberAndYear,
} from '../../../shared/libraries/utils/date'
import { getCampaignsWithNpsTableData } from './gql'
import { headers } from './options'

import { styles } from './styles'

const NetPromoterScore = ({ classes }) => {
  const [country, setCountry] = useState()
  const [client, setClient] = useState(null)
  const [allCampaigns, setAllCampaigns] = useState(null)
  const [campaignRows, setCampaignRows] = useState([])
  const [filteredRows, setFilteredRows] = useState([])
  const [isTableLoading, setIsTableLoading] = useState(false)
  const [descriptionText, setDescriptionText] = useState(
    'Please select country',
  )

  const dispatch = useDispatch()
  const countries = useSelector(state => state.countries.countriesList) || []

  useEffect(() => {
    dispatch(getCountries())
  }, [])

  useEffect(() => {
    if (country) {
      setDescriptionText('...Loading')
      getAllCampaignsWithNps(country)
    }
  }, [country])

  useEffect(() => {
    if (client) {
      setFilteredRows(campaignRows.filter(r => r.Client == client))
    } else {
      setFilteredRows(campaignRows)
    }
  }, [campaignRows, client])

  const getAllCampaignsWithNps = async country => {
    setIsTableLoading(true)

    const { data } = await getCampaignsWithNpsTableData({
      countryIds: country,
      hasReferralProgram: true,
    }).then(response => response.json())

    const allCampaigns = data.campaigns.edges.map(item => item.node)

    setAllCampaigns(allCampaigns)
    const clientReferralPrograms = data.campaigns.edges.map(
      item => item.node.clientReferralProgram,
    )
    if (clientReferralPrograms.length !== 0) {
      const netPromoterScoreData = clientReferralPrograms.reduce(
        (acc, clientReferralProgram) => {
          if (clientReferralProgram && isDefined(clientReferralProgram.score)) {
            acc.sum += clientReferralProgram.score
            acc.votesAmount += 1
          }
          return acc
        },
        { sum: 0, votesAmount: 0 },
      )
      if (netPromoterScoreData.votesAmount) {
        const averageNetPromoterScore =
          netPromoterScoreData.sum / netPromoterScoreData.votesAmount
        setDescriptionText(
          `Average Net Promoter Score: ${averageNetPromoterScore.toFixed(1)}`,
        )
      } else {
        setDescriptionText('No clients voted yet')
      }
    } else {
      setDescriptionText('No campaigns with NPS scores in this country')
    }

    const rows = await allCampaigns.reduce(async (acc, row) => {
      const getUser = async id => {
        const req = await apiClient.getUserById(id)
        const res = await req.json()

        return `${res.firstName} ${res.lastName}`
      }
      const accumulator = await acc.then()
      const calculateWeekDuration = missions => {
        const missionsDate = []
        missions.forEach(({ node: mission }) => {
          const date = new Date(dropTimeZoneFromDate(mission.dateTime))
          const { weekNumber, year } = getWeekNumberAndYear(date)
          missionsDate.push(year + '-' + weekNumber)
        })
        return [...new Set(missionsDate)].length
      }

      if (row => row.countryId == country) {
        const missions = row.missions.edges.filter(
          m => m.node.cancelled == false,
        )
        const user =
          row.clientReferralProgram &&
          (await getUser(row.clientReferralProgram.voterId))

        accumulator.push({
          'Client': row.client.name,
          'Campaign': row.name,
          'Type': row.type,
          'Duration(W)': calculateWeekDuration(row.missions.edges) || '-',
          'Missions': String(missions.length),
          'Rating': row.clientReferralProgram
            ? row.clientReferralProgram.score
            : '-',
          'User': row.clientReferralProgram.voterId ? user : '-',
          'Date': row.clientReferralProgram
            ? row.clientReferralProgram.referralEmailDate
              ? moment(row.clientReferralProgram.referralEmailDate).format(
                  'DD/MM/YYYY',
                )
              : '-'
            : '-',
          'Feedback': row.clientReferralProgram.feedback
            ? row.clientReferralProgram.feedback
            : '-',
          'Tags': row.client.tags.length
            ? row.client.tags
                .map(({ value }, index) => `№${index + 1} ${value}`)
                .join(' | ')
            : '-',
        })
      }
      return Promise.resolve(accumulator)
    }, Promise.all([]))
    await setCampaignRows(rows)
    setIsTableLoading(false)
  }

  const action = data => {
    const campaignId = allCampaigns.filter(c => c.name == data.Campaign)[0].id
    window
      .open(`/dashboard/campaigns/${campaignId}/dashboard`, '_blank')
      .focus()
  }

  const onExportData = () => {
    if (filteredRows.length) {
      let content = headers.join(',')
      filteredRows.forEach(row => {
        const newRow = headers.map(headerName => {
          const value = row[headerName]
          return typeof value === 'string' ? escapeCsvString(value) : value
        })
        content += '\n' + newRow.join(',')
      })
      downloadStringAsFile(content, `export_net_promoter_score.csv`, 'text/csv')
    }
  }
  return (
    <Fragment>
      <BreadcrumbsNetPromoterScore className={classes.header} />
      <PageTitle className={classes.header}>Net Promoter Score</PageTitle>
      <div className={classes.wrapper}>
        <FormControl margin="normal">
          <InputLabel shrink>Country</InputLabel>
          <Select
            id="country-select-nps"
            name="countryId"
            displayEmpty
            value={country ? country : ''}
            onChange={e => setCountry(e.target.value)}
          >
            {countries.length !== 0 ? (
              countries.map(country => (
                <MenuItem value={country.id} key={country.id}>
                  {country.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>Loading...</MenuItem>
            )}
          </Select>
        </FormControl>
        {country && (
          <div className={classes.wrapperClientSelect}>
            <FormControl margin="normal">
              <InputLabel shrink>Client</InputLabel>
              <Select
                id="country-select-nps"
                name="clientId"
                displayEmpty
                value={client ? client : ''}
                onChange={e => setClient(e.target.value)}
                disabled={!campaignRows.length}
              >
                <MenuItem value={''}>ALL</MenuItem>
                {campaignRows && campaignRows.length ? (
                  campaignRows.map(client => (
                    <MenuItem value={client.Client}>{client.Client}</MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>Loading...</MenuItem>
                )}
              </Select>
            </FormControl>
          </div>
        )}
        <div className={classes.result}>{descriptionText}</div>
        <div className={classes.exportButton}>
          <Button disabled={!filteredRows.length} onClick={onExportData}>
            Export
          </Button>
        </div>
      </div>
      {isTableLoading ? (
        <LoadingIndicator style={{ height: 'inherit', marginTop: '50px' }} />
      ) : !filteredRows.length ? null : (
        <SimpleTableTable
          className={classes.table}
          header={headers}
          data={filteredRows}
          showPages={false}
          action={action}
        />
      )}
    </Fragment>
  )
}

export default injectStyles(styles)(NetPromoterScore)
